import React from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  Box,
  Link,
} from '@mui/material';
import { useTranslation } from 'react-i18next';

interface PrivacyPolicyProps {
  open: boolean;
  onClose: () => void;
}

export const PrivacyPolicy = ({ open, onClose }: PrivacyPolicyProps) => {
  const { t } = useTranslation();

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth scroll="paper">
      <DialogTitle sx={{ bgcolor: '#667eea', color: 'white', fontWeight: 600 }}>
        {t('privacy.title', 'Privacy Policy')}
      </DialogTitle>
      <DialogContent dividers sx={{ mt: 2 }}>
        {/* Last Updated */}
        <Typography variant="caption" color="text.secondary" gutterBottom display="block">
          {t('privacy.lastUpdated', 'Last Updated')}: January 6, 2025
        </Typography>

        {/* Introduction */}
        <Box sx={{ mb: 3, mt: 2 }}>
          <Typography variant="h6" gutterBottom sx={{ fontWeight: 600, color: '#667eea' }}>
            {t('privacy.intro.title', '1. Introduction')}
          </Typography>
          <Typography variant="body2" paragraph>
            {t(
              'privacy.intro.text',
              'GetEmoji ("we", "our", or "us") respects your privacy. This Privacy Policy explains how we collect, use, and protect your information when you use our emoji browser and copy tool.'
            )}
          </Typography>
        </Box>

        {/* Data We Collect */}
        <Box sx={{ mb: 3 }}>
          <Typography variant="h6" gutterBottom sx={{ fontWeight: 600, color: '#667eea' }}>
            {t('privacy.dataCollected.title', '2. Data We Collect')}
          </Typography>
          <Typography variant="body2" paragraph>
            {t('privacy.dataCollected.text', 'With your consent, we collect:')}
          </Typography>
          <Typography variant="body2" component="div">
            <ul style={{ marginTop: 0 }}>
              <li>
                {t(
                  'privacy.dataCollected.usage',
                  'Anonymous usage statistics (page views, session duration)'
                )}
              </li>
              <li>
                {t('privacy.dataCollected.emojis', 'Emoji copy events (which emojis are popular)')}
              </li>
              <li>
                {t(
                  'privacy.dataCollected.searches',
                  'Search queries (to improve search functionality)'
                )}
              </li>
              <li>{t('privacy.dataCollected.language', 'Language preferences')}</li>
              <li>
                {t(
                  'privacy.dataCollected.location',
                  'Geographic region (country-level, no precise location)'
                )}
              </li>
              <li>
                {t(
                  'privacy.dataCollected.device',
                  'Device type (mobile/desktop) and browser information'
                )}
              </li>
            </ul>
          </Typography>
        </Box>

        {/* Data We Don't Collect */}
        <Box sx={{ mb: 3 }}>
          <Typography variant="h6" gutterBottom sx={{ fontWeight: 600, color: '#667eea' }}>
            {t('privacy.dataNotCollected.title', '3. Data We Do NOT Collect')}
          </Typography>
          <Typography variant="body2" component="div">
            <ul style={{ marginTop: 0 }}>
              <li>
                {t(
                  'privacy.dataNotCollected.personal',
                  'Personal identifiable information (names, email addresses)'
                )}
              </li>
              <li>
                {t('privacy.dataNotCollected.ip', 'Exact IP addresses (we anonymize all IPs)')}
              </li>
              <li>{t('privacy.dataNotCollected.cookies', 'Third-party tracking cookies')}</li>
              <li>{t('privacy.dataNotCollected.crossSite', 'Cross-site tracking data')}</li>
            </ul>
          </Typography>
        </Box>

        {/* How We Use Data */}
        <Box sx={{ mb: 3 }}>
          <Typography variant="h6" gutterBottom sx={{ fontWeight: 600, color: '#667eea' }}>
            {t('privacy.usage.title', '4. How We Use Your Data')}
          </Typography>
          <Typography variant="body2" component="div">
            <ul style={{ marginTop: 0 }}>
              <li>{t('privacy.usage.improve', 'Improve user experience and app functionality')}</li>
              <li>
                {t(
                  'privacy.usage.understand',
                  'Understand which emojis and features are most popular'
                )}
              </li>
              <li>{t('privacy.usage.optimize', 'Optimize search and performance')}</li>
              <li>{t('privacy.usage.analytics', 'Generate anonymous usage statistics')}</li>
            </ul>
          </Typography>
        </Box>

        {/* Third-Party Services */}
        <Box sx={{ mb: 3 }}>
          <Typography variant="h6" gutterBottom sx={{ fontWeight: 600, color: '#667eea' }}>
            {t('privacy.thirdParty.title', '5. Third-Party Services')}
          </Typography>
          <Typography variant="body2" paragraph>
            {t('privacy.thirdParty.text', 'We use the following third-party services:')}
          </Typography>
          <Typography variant="body2" component="div">
            <ul style={{ marginTop: 0 }}>
              <li>
                <strong>Google Analytics 4</strong> -{' '}
                {t('privacy.thirdParty.analytics', 'For usage analytics and statistics')}
                <br />
                <Link
                  href="https://policies.google.com/privacy"
                  target="_blank"
                  rel="noopener"
                  sx={{ fontSize: '0.875rem' }}
                >
                  {t('privacy.thirdParty.googlePrivacy', 'Google Privacy Policy')}
                </Link>
              </li>
              <li>
                <strong>Twemoji (Twitter)</strong> -{' '}
                {t('privacy.thirdParty.emoji', 'For emoji rendering (CDN only, no tracking)')}
              </li>
            </ul>
          </Typography>
        </Box>

        {/* Your Rights (GDPR) */}
        <Box sx={{ mb: 3 }}>
          <Typography variant="h6" gutterBottom sx={{ fontWeight: 600, color: '#667eea' }}>
            {t('privacy.rights.title', '6. Your Rights (GDPR)')}
          </Typography>
          <Typography variant="body2" paragraph>
            {t('privacy.rights.text', 'Under GDPR, you have the right to:')}
          </Typography>
          <Typography variant="body2" component="div">
            <ul style={{ marginTop: 0 }}>
              <li>{t('privacy.rights.access', 'Access your data')}</li>
              <li>{t('privacy.rights.rectification', 'Correct inaccurate data')}</li>
              <li>{t('privacy.rights.erasure', 'Request deletion of your data')}</li>
              <li>{t('privacy.rights.restriction', 'Restrict processing of your data')}</li>
              <li>{t('privacy.rights.portability', 'Data portability')}</li>
              <li>{t('privacy.rights.object', 'Object to processing')}</li>
              <li>{t('privacy.rights.withdraw', 'Withdraw consent at any time')}</li>
            </ul>
          </Typography>
          <Typography variant="body2" paragraph>
            {t(
              'privacy.rights.how',
              'To exercise these rights, clear your browser localStorage or contact us.'
            )}
          </Typography>
        </Box>

        {/* Cookies */}
        <Box sx={{ mb: 3 }}>
          <Typography variant="h6" gutterBottom sx={{ fontWeight: 600, color: '#667eea' }}>
            {t('privacy.cookies.title', '7. Cookies')}
          </Typography>
          <Typography variant="body2" paragraph>
            {t('privacy.cookies.text', 'We use cookies for:')}
          </Typography>
          <Typography variant="body2" component="div">
            <ul style={{ marginTop: 0 }}>
              <li>
                {t(
                  'privacy.cookies.preferences',
                  'Storing your language and skin tone preferences'
                )}
              </li>
              <li>{t('privacy.cookies.consent', 'Remembering your cookie consent choice')}</li>
              <li>{t('privacy.cookies.analytics', 'Analytics (only if you consent)')}</li>
            </ul>
          </Typography>
          <Typography variant="body2" paragraph>
            {t(
              'privacy.cookies.manage',
              'You can delete cookies anytime through your browser settings.'
            )}
          </Typography>
        </Box>

        {/* Data Security */}
        <Box sx={{ mb: 3 }}>
          <Typography variant="h6" gutterBottom sx={{ fontWeight: 600, color: '#667eea' }}>
            {t('privacy.security.title', '8. Data Security')}
          </Typography>
          <Typography variant="body2" paragraph>
            {t(
              'privacy.security.text',
              'We implement industry-standard security measures to protect your data. All data transmission is encrypted via HTTPS. However, no method of transmission over the Internet is 100% secure.'
            )}
          </Typography>
        </Box>

        {/* Data Retention */}
        <Box sx={{ mb: 3 }}>
          <Typography variant="h6" gutterBottom sx={{ fontWeight: 600, color: '#667eea' }}>
            {t('privacy.retention.title', '9. Data Retention')}
          </Typography>
          <Typography variant="body2" paragraph>
            {t(
              'privacy.retention.text',
              'Analytics data is retained for 14 months by Google Analytics. You can request deletion at any time.'
            )}
          </Typography>
        </Box>

        {/* Children's Privacy */}
        <Box sx={{ mb: 3 }}>
          <Typography variant="h6" gutterBottom sx={{ fontWeight: 600, color: '#667eea' }}>
            {t('privacy.children.title', "10. Children's Privacy")}
          </Typography>
          <Typography variant="body2" paragraph>
            {t(
              'privacy.children.text',
              'Our service is safe for all ages. We do not knowingly collect personal information from anyone. If you are a parent or guardian and believe we have collected information about a child, please contact us.'
            )}
          </Typography>
        </Box>

        {/* Changes to Policy */}
        <Box sx={{ mb: 3 }}>
          <Typography variant="h6" gutterBottom sx={{ fontWeight: 600, color: '#667eea' }}>
            {t('privacy.changes.title', '11. Changes to This Policy')}
          </Typography>
          <Typography variant="body2" paragraph>
            {t(
              'privacy.changes.text',
              'We may update this Privacy Policy from time to time. We will notify you of any changes by posting the new policy on this page with an updated "Last Updated" date.'
            )}
          </Typography>
        </Box>

        {/* Contact */}
        <Box sx={{ mb: 2 }}>
          <Typography variant="h6" gutterBottom sx={{ fontWeight: 600, color: '#667eea' }}>
            {t('privacy.contact.title', '12. Contact Us')}
          </Typography>
          <Typography variant="body2" paragraph>
            {t(
              'privacy.contact.text',
              'If you have questions about this Privacy Policy or want to exercise your rights, please contact us at:'
            )}
          </Typography>
          <Typography variant="body2">
            Email: <Link href="mailto:privacy@getemoji.ai">privacy@getemoji.ai</Link>
          </Typography>
        </Box>
      </DialogContent>
      <DialogActions sx={{ p: 2 }}>
        <Button onClick={onClose} variant="contained" sx={{ bgcolor: '#667eea' }}>
          {t('privacy.close', 'Close')}
        </Button>
      </DialogActions>
    </Dialog>
  );
};
